import { useState } from "react";
import { CheckCircle2, Building2, Truck, Tags, Percent } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const empty = { business_name: "", contact_name: "", phone: "", email: "", city: "", product_interest: "", quantity: "", message: "" };

export default function Wholesale() {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_name || !form.phone) return toast.error("Business name, contact name and phone are required");
    setSending(true);
    try {
      await api.post("/wholesale", form);
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div>
      <section className="bc-dusty">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16 text-center" data-aos="fade-up">
          <p className="text-xs uppercase tracking-luxe text-foreground/70">B2B · Bulk Orders</p>
          <h1 className="font-display text-4xl sm:text-5xl font-light mt-3">Wholesale Enquiry</h1>
          <p className="text-foreground/70 mt-4 max-w-lg mx-auto font-light">Partner with BODYCRUSH by Gujarish. Share your requirement and our team will get back with wholesale pricing and catalogues.</p>
        </div>
      </section>

      <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-16 grid lg:grid-cols-[1fr_320px] gap-12">
        {done ? (
          <div className="text-center py-16" data-testid="wholesale-success">
            <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500" />
            <h2 className="font-display text-3xl font-light mt-6">Enquiry received!</h2>
            <p className="text-muted-foreground mt-2 font-light">Thank you. Our wholesale team will contact you shortly on {form.phone}.</p>
            <button onClick={() => { setForm(empty); setDone(false); }} className="mt-8 border border-foreground px-8 py-3.5 text-xs uppercase tracking-[0.2em] hover:bg-foreground hover:text-[hsl(var(--ivory))] transition-colors" data-testid="wholesale-another">Submit another</button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-card border border-border p-8" data-testid="wholesale-form">
            <h2 className="font-display text-2xl font-light mb-6">Your details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Business / Store Name*" value={form.business_name} onChange={set("business_name")} testid="wh-business" />
              <F label="Contact Person*" value={form.contact_name} onChange={set("contact_name")} testid="wh-contact" />
              <F label="Phone*" value={form.phone} onChange={set("phone")} testid="wh-phone" />
              <F label="Email" value={form.email} onChange={set("email")} testid="wh-email" />
              <F label="City" value={form.city} onChange={set("city")} testid="wh-city" />
              <F label="Approx. Quantity (e.g. 500 pcs)" value={form.quantity} onChange={set("quantity")} testid="wh-qty" />
            </div>
            <div className="mt-4">
              <F label="Products of Interest (e.g. Panties, Camisoles)" value={form.product_interest} onChange={set("product_interest")} testid="wh-interest" />
            </div>
            <div className="mt-4">
              <label className="text-sm font-medium">Message</label>
              <textarea value={form.message} onChange={set("message")} rows={4} data-testid="wh-message" placeholder="Tell us about your requirement…"
                className="mt-1.5 w-full border border-input bg-[hsl(var(--ivory))] px-3 py-2.5 text-sm outline-none focus:border-primary" />
            </div>
            <button disabled={sending} className="mt-6 w-full sm:w-auto bg-foreground text-[hsl(var(--ivory))] px-10 py-4 text-xs uppercase tracking-[0.2em] hover:bg-primary transition-colors disabled:opacity-60" data-testid="wholesale-submit">
              {sending ? "Sending…" : "Send Enquiry"}
            </button>
          </form>
        )}

        <aside className="space-y-6">
          <Perk icon={Percent} title="Wholesale Pricing" sub="Best rates on bulk orders" />
          <Perk icon={Tags} title="Full Catalogue" sub="Panties, camisoles & more" />
          <Perk icon={Truck} title="Pan-India Supply" sub="Reliable dispatch" />
          <Perk icon={Building2} title="Retailer Friendly" sub="Ready-to-sell packaging" />
        </aside>
      </div>
    </div>
  );
}

const F = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-sm font-medium">{label}</label>
    <input value={value} onChange={onChange} data-testid={testid} className="mt-1.5 w-full border border-input bg-[hsl(var(--ivory))] px-3 py-2.5 text-sm outline-none focus:border-primary" />
  </div>
);

const Perk = ({ icon: Icon, title, sub }) => (
  <div className="flex items-center gap-3 bg-card border border-border p-4">
    <Icon className="w-6 h-6 text-primary shrink-0" strokeWidth={1.4} />
    <div>
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-muted-foreground">{sub}</p>
    </div>
  </div>
);
